import { Helmet } from 'react-helmet-async'
import HomeTemplate from '../templates/HomeTemplate.jsx'
import VideoDownloaderSEO from '../components/ui/VideoDownloaderSEO.jsx'
import { SITE_URL } from '../utils/seo.js'
import { useLanguage } from '../context/LanguageContext.jsx'

const title       = 'Facebook Video Downloader — Free HD MP4 & MP3 Download | FastAudio'
const description = 'Download Facebook videos, Watch posts and Reels as HD MP4 or MP3 audio — free, no Facebook account required. Works on iPhone, Android & PC.'
const canonical   = `${SITE_URL}/facebook-video-downloader`

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Can I download Facebook videos as MP4?',           acceptedAnswer: { '@type': 'Answer', text: 'Yes. FastAudio downloads public Facebook videos as MP4 in SD (480p) or HD (720p) quality.' } },
    { '@type': 'Question', name: 'Is the Facebook downloader free?',                 acceptedAnswer: { '@type': 'Answer', text: 'Yes, completely free with no Facebook account or registration required.' } },
    { '@type': 'Question', name: 'Can I download Facebook Reels?',                   acceptedAnswer: { '@type': 'Answer', text: 'Yes. Publicly accessible Facebook Reels can be downloaded as MP4 video or MP3 audio.' } },
    { '@type': 'Question', name: 'How do I get the Facebook video URL?',             acceptedAnswer: { '@type': 'Answer', text: 'Click the three-dot (⋯) menu on the post → Copy link. On mobile, tap Share → Copy link.' } },
    { '@type': 'Question', name: 'Can I download private Facebook videos?',          acceptedAnswer: { '@type': 'Answer', text: 'No. Only publicly visible videos without a Facebook login can be downloaded.' } },
  ],
}

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Download Facebook Videos as MP4',
  step: [
    { '@type': 'HowToStep', name: 'Find the Facebook video', text: 'Open the video post on Facebook.' },
    { '@type': 'HowToStep', name: 'Copy the video URL',      text: 'Click ⋯ on the post → Copy link.' },
    { '@type': 'HowToStep', name: 'Paste into FastAudio',    text: 'Paste the URL into the FastAudio search box.' },
    { '@type': 'HowToStep', name: 'Choose HD or SD',         text: 'Select 720p HD or 480p SD for MP4, or an MP3 quality for audio.' },
    { '@type': 'HowToStep', name: 'Click Download',          text: 'Hit ⚡ Download — the file saves to your device.' },
  ],
}

export default function FacebookVideoDownloader() {
  const { t } = useLanguage()
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title"       content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url"         content={canonical} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>
      <HomeTemplate
        provider="facebook"
        title={title}
        description={description}
        canonical={canonical}
        badge="Facebook Video Downloader"
        heading={<>Facebook Video <span className="text-gradient">Downloader HD</span></>}
        subheading="Download Facebook videos, Watch posts and Reels as HD MP4 (720p) or MP3 audio — no Facebook account required, completely free."
        placeholder={t('fbPlaceholder')}
      >
        <VideoDownloaderSEO provider="facebook" />
      </HomeTemplate>
    </>
  )
}
